import { useAutenticacion } from './authContext';
import type { IPermiso } from '../../modelos/Autenticacion/entidades/IPermiso';

/**
 * Hook personalizado para manejar validaciones de permisos
 */
export const usePermisos = () => {
    const { usuario } = useAutenticacion();

    /**
     * Verifica si el usuario tiene un permiso específico
     * @param codigoPermiso - El código del permiso a verificar
     * @returns true si el usuario tiene el permiso, false en caso contrario
     */
    const tienePermiso = (codigoPermiso: string): boolean => {
        if (!usuario?.Permisos) {
            return false;
        }

        return usuario.Permisos.some((permiso: IPermiso) => permiso.Codigo === codigoPermiso);
    };

    /**
     * Verifica si el usuario tiene alguno de los permisos especificados
     * @param codigosPermisos - Array de códigos de permisos a verificar
     * @returns true si el usuario tiene al menos uno de los permisos, false en caso contrario
     */
    const tieneAlgunPermiso = (codigosPermisos: string[]): boolean => {
        if (!usuario?.Permisos) {
            return false;
        }

        return codigosPermisos.some(codigo => tienePermiso(codigo));
    };

    /**
     * Verifica si el usuario tiene todos los permisos especificados
     * @param codigosPermisos - Array de códigos de permisos a verificar
     * @returns true si el usuario tiene todos los permisos, false en caso contrario
     */
    const tieneTodosLosPermisos = (codigosPermisos: string[]): boolean => {
        if (!usuario?.Permisos) {
            return false;
        }

        return codigosPermisos.every(codigo => tienePermiso(codigo));
    };

    /**
     * Obtiene los permisos del usuario autenticado
     * @returns Array de permisos del usuario
     */
    const obtenerPermisos = () => {
        return usuario?.Permisos || [];
    };

    /**
     * Verifica si el usuario puede acceder a un módulo específico
     * @param permisoRequerido - El permiso requerido para acceder al módulo
     * @returns true si puede acceder, false en caso contrario
     */
    const puedeAccederModulo = (permisoRequerido?: string): boolean => {
        if (!permisoRequerido) {
            return true; // Si no se requiere permiso específico, permitir acceso
        }
        
        return tienePermiso(permisoRequerido);
    };

    return {
        tienePermiso,
        tieneAlgunPermiso,
        tieneTodosLosPermisos,
        obtenerPermisos,
        puedeAccederModulo
    };
};
